import React, { useState, useEffect } from 'react';
import { FaTimes, FaServer, FaCode, FaWordpress, FaCopy, FaSpinner, FaInfoCircle, FaCheck, FaGlobe, FaWindowMaximize, FaPlug } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { post } from '../../helpers/apiHelper';

const EmbedFormOptionsModal = ({ isOpen, onClose, marca, tiendas }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tokenData, setTokenData] = useState(null);

    const options = [
        {
            id: 'endpoint',
            title: 'Endpoint API',
            icon: FaServer,
            description: 'Integración directa con tu backend. Envía datos mediante POST request.',
            color: 'blue',
            badge: 'Para desarrolladores',
            details: 'Ideal si tienes control total sobre tu servidor y quieres personalizar completamente el formulario.'
        },
        {
            id: 'iframe',
            title: 'Iframe',
            icon: FaWindowMaximize,
            description: 'Copia y pega un código HTML simple. Funciona en cualquier sitio web.',
            color: 'green',
            badge: 'Fácil de usar',
            details: 'Perfecto para sitios web estáticos, HTML simple, o cuando necesitas una solución rápida sin código.'
        }
        // Shortcode WordPress - Comentado hasta que se cree el plugin
        // {
        //     id: 'shortcode',
        //     title: 'Shortcode WordPress',
        //     icon: FaWordpress,
        //     description: 'Shortcode listo para usar en WordPress y otros CMS compatibles.',
        //     color: 'purple',
        //     badge: 'WordPress',
        //     details: 'Solo copia el shortcode y pégalo en cualquier página o entrada de WordPress.'
        // }
    ];

    const handleClose = () => {
        setSelectedOption(null);
        setTokenData(null);
        onClose();
    };

    const generateToken = async () => {
        if (!marca || tiendas.length === 0) {
            Swal.fire({
                title: 'Error',
                text: 'Necesitas tener al menos una marca y una tienda configuradas',
                icon: 'error'
            });
            return;
        }

        setLoading(true);
        try {
            const tokenPayload = {
                marcaId: marca._id,
                tiendaId: tiendas[0]?._id, // Primera tienda por defecto, puede ser opcional
                allowedOrigins: [], // Dominios vacíos - permite cualquier origen
                theme: 'light',
                lang: 'es',
                expiresIn: 0 // Token permanente (sin expiración)
            };

            const response = await post('clientes/embed-token', tokenPayload);

            if (response.success && response.data) {
                setTokenData(response.data);
            } else {
                throw new Error(response.error || 'Error al generar el token');
            }
        } catch (error) {
            console.error('Error generating token:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo generar el token. Verifica tu conexión.',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Generar token automáticamente cuando se selecciona iframe
    useEffect(() => {
        // Solo generar si se selecciona iframe, no hay token aún, no está cargando, y hay datos necesarios
        if (selectedOption === 'iframe' && !tokenData && !loading && marca && tiendas.length > 0) {
            const generateAuto = async () => {
                setLoading(true);
                try {
                    const tokenPayload = {
                        marcaId: marca._id,
                        tiendaId: tiendas[0]?._id,
                        allowedOrigins: [],
                        theme: 'light',
                        lang: 'es',
                        expiresIn: 0
                    };

                    const response = await post('clientes/embed-token', tokenPayload);

                    if (response.success && response.data) {
                        setTokenData(response.data);
                    } else {
                        throw new Error(response.error || 'Error al generar el token');
                    }
                } catch (error) {
                    console.error('Error generating token:', error);
                    Swal.fire({
                        title: 'Error',
                        text: error.message || 'No se pudo generar el token. Verifica tu conexión.',
                        icon: 'error'
                    });
                } finally {
                    setLoading(false);
                }
            };
            generateAuto();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption]);

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            Swal.fire({
                title: '¡Copiado!',
                text: 'Código copiado al portapapeles',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error copying to clipboard:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo copiar al portapapeles',
                icon: 'error'
            });
        }
    };

    const getEndpointCode = () => {
        const apiUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.petitprime.com/api';
        return `${apiUrl}/clientes/embed/register`;
    };

    const getIframeCode = () => {
        if (!tokenData?.code) return '';
        // Usar siempre la URL de producción de petitprime, no window.location.origin
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://petitprime.com';
        const embedUrl = `${frontendUrl}/embed/registro?code=${tokenData.code}`;
        // TRUSTED_ORIGIN siempre debe ser petitprime, no el origen del sitio host
        const TRUSTED_ORIGIN = frontendUrl;

        return `<iframe
    id="petitprime-register"
    src="${embedUrl}"
    style="width:100%;min-height:620px;border:0;"
    loading="lazy"
    referrerpolicy="no-referrer"
    sandbox="allow-forms allow-scripts allow-same-origin"
    allow="clipboard-write"
></iframe>
<script>
    (function () {
        var iframe = document.getElementById('petitprime-register');
        var TRUSTED_ORIGIN = '${TRUSTED_ORIGIN}';

        window.addEventListener('message', function (e) {
            // Validación estricta: origen Y source
            if (e.origin !== TRUSTED_ORIGIN) return;
            if (e.source !== iframe.contentWindow) return;
            if (!e.data || typeof e.data !== 'object') return;

            switch (e.data.type) {
                case 'resize':
                    if (typeof e.data.height === 'number') {
                        iframe.style.height = e.data.height + 'px';
                    }
                    break;
                case 'success':
                    console.log('Registro exitoso:', e.data.payload);
                    // Opcional: mostrar notificación
                    alert('¡Registro exitoso! Bienvenido a nuestro programa de fidelización.');
                    break;
                case 'error':
                    console.error('Error en registro:', e.data.error);
                    alert('Error: ' + (e.data.error || 'desconocido'));
                    break;
            }
        });
    })();
</script>`;
    };

    const getShortcode = () => {
        if (!tokenData?.code) return '';
        return `[petitprime_iframe code="${tokenData.code}"]`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
                    <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 pb-4 border-b border-gray-200">
                            <div className="flex-1">
                                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center flex-wrap">
                                    <FaGlobe className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-orange-500" />
                                    <span className="break-words">Añadir Formulario en Web Propia</span>
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                    Elige el método de integración que mejor se adapte a tu sitio web
                                </p>
                            </div>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0"
                            >
                                <FaTimes className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>

                        {/* Opciones */}
                        {!selectedOption && (
                            <div className="mt-4 sm:mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                                {options.map((option) => {
                                    const Icon = option.icon;
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => setSelectedOption(option.id)}
                                            className={`relative p-4 sm:p-6 rounded-lg border-2 transition-all hover:shadow-lg text-left w-full ${option.color === 'blue'
                                                ? 'border-blue-200 hover:border-blue-400 bg-blue-50'
                                                : option.color === 'green'
                                                    ? 'border-green-200 hover:border-green-400 bg-green-50'
                                                    : 'border-purple-200 hover:border-purple-400 bg-purple-50'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${option.color === 'blue'
                                                    ? 'bg-blue-100'
                                                    : option.color === 'green'
                                                        ? 'bg-green-100'
                                                        : 'bg-purple-100'
                                                    }`}>
                                                    <Icon className={`text-xl sm:text-2xl ${option.color === 'blue'
                                                        ? 'text-blue-600'
                                                        : option.color === 'green'
                                                            ? 'text-green-600'
                                                            : 'text-purple-600'
                                                        }`} />
                                                </div>
                                                <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ml-2 ${option.color === 'blue'
                                                    ? 'bg-blue-200 text-blue-800'
                                                    : option.color === 'green'
                                                        ? 'bg-green-200 text-green-800'
                                                        : 'bg-purple-200 text-purple-800'
                                                    }`}>
                                                    {option.badge}
                                                </span>
                                            </div>
                                            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                                                {option.title}
                                            </h4>
                                            <p className="text-xs sm:text-sm text-gray-600 mb-3">
                                                {option.description}
                                            </p>
                                            <div className="flex items-start text-xs text-gray-500">
                                                <FaInfoCircle className="mr-1 flex-shrink-0 mt-0.5" />
                                                <span className="break-words">{option.details}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Vista de Endpoint */}
                        {selectedOption === 'endpoint' && (
                            <div className="mt-4 sm:mt-6">
                                <button
                                    onClick={() => setSelectedOption(null)}
                                    className="text-blue-600 hover:text-blue-800 mb-4 flex items-center text-sm sm:text-base"
                                >
                                    <FaTimes className="mr-2" />
                                    Volver
                                </button>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <h4 className="text-lg font-semibold text-blue-800 mb-2 flex items-center">
                                        <FaServer className="mr-2" />
                                        Integración mediante Endpoint API
                                    </h4>
                                    <p className="text-sm text-blue-700">
                                        Este método requiere que implementes tu propio formulario y envíes los datos directamente al endpoint.
                                    </p>
                                </div>
                                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-yellow-400">POST</span>
                                        <button
                                            onClick={() => copyToClipboard(getEndpointCode())}
                                            className="px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600 flex items-center"
                                        >
                                            <FaCopy className="mr-1" />
                                            Copiar
                                        </button>
                                    </div>
                                    <div className="text-white break-all">{getEndpointCode()}</div>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-sm text-yellow-800 mb-2">
                                        <strong>Nota:</strong> Para ver la documentación completa del endpoint, haz clic en el botón de abajo.
                                    </p>
                                    <button
                                        onClick={() => {
                                            handleClose();
                                            window.location.href = '/create-form';
                                        }}
                                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Ver Documentación Completa del Endpoint
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Vista de Iframe */}
                        {selectedOption === 'iframe' && (
                            <div className="mt-4 sm:mt-6">
                                <button
                                    onClick={() => setSelectedOption(null)}
                                    className="text-green-600 hover:text-green-800 mb-4 flex items-center text-sm sm:text-base"
                                >
                                    <FaTimes className="mr-2" />
                                    Volver
                                </button>

                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-8">
                                        <FaSpinner className="w-8 h-8 text-green-600 animate-spin mb-4" />
                                        <p className="text-gray-600">Generando código iframe...</p>
                                    </div>
                                ) : tokenData ? (
                                    <>
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-lg font-semibold text-green-800 mb-1 flex items-center">
                                                        <FaCheck className="mr-2" />
                                                        Código Iframe Generado
                                                    </h4>
                                                    <p className="text-sm text-green-700">
                                                        Copia y pega este código en tu sitio web
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setTokenData(null);
                                                    }}
                                                    className="text-green-600 hover:text-green-800 text-sm"
                                                >
                                                    Generar nuevo
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg font-mono text-xs sm:text-sm mb-4 overflow-x-auto">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2">
                                                <span className="text-yellow-400 text-xs sm:text-sm">HTML</span>
                                                <button
                                                    onClick={() => copyToClipboard(getIframeCode())}
                                                    className="px-2 sm:px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600 flex items-center w-full sm:w-auto justify-center sm:justify-start"
                                                >
                                                    <FaCopy className="mr-1" />
                                                    Copiar Todo
                                                </button>
                                            </div>
                                            <pre className="text-xs sm:text-sm whitespace-pre-wrap break-all overflow-x-auto">{getIframeCode()}</pre>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h5 className="text-sm font-semibold text-blue-800 mb-2">📋 Instrucciones:</h5>
                                            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                                                <li>Copia el código completo de arriba</li>
                                                <li>Pégalo en el HTML de tu página donde quieras mostrar el formulario</li>
                                                <li>El iframe se ajustará automáticamente al tamaño del contenido</li>
                                                <li>Recibirás notificaciones cuando un cliente se registre exitosamente</li>
                                            </ol>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        )}

                        {/* Vista de Shortcode - Comentado hasta que se cree el plugin */}
                        {/* {selectedOption === 'shortcode' && (
                            <div className="mt-6">
                                <button
                                    onClick={() => setSelectedOption(null)}
                                    className="text-purple-600 hover:text-purple-800 mb-4 flex items-center"
                                >
                                    <FaTimes className="mr-2" />
                                    Volver
                                </button>
                                
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-8">
                                        <FaSpinner className="w-8 h-8 text-purple-600 animate-spin mb-4" />
                                        <p className="text-gray-600">Generando shortcode...</p>
                                    </div>
                                ) : tokenData ? (
                                    <>
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-lg font-semibold text-purple-800 mb-1 flex items-center">
                                                        <FaCheck className="mr-2" />
                                                        Shortcode Generado
                                                    </h4>
                                                    <p className="text-sm text-purple-700">
                                                        Copia este shortcode y pégalo en cualquier página o entrada de WordPress
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setTokenData(null);
                                                    }}
                                                    className="text-purple-600 hover:text-purple-800 text-sm"
                                                >
                                                    Generar nuevo
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm mb-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-yellow-400">SHORTCODE</span>
                                                <button
                                                    onClick={() => copyToClipboard(getShortcode())}
                                                    className="px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600 flex items-center"
                                                >
                                                    <FaCopy className="mr-1" />
                                                    Copiar
                                                </button>
                                            </div>
                                            <div className="text-white text-lg font-semibold">{getShortcode()}</div>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                            <h5 className="text-sm font-semibold text-blue-800 mb-2">📋 Instrucciones:</h5>
                                            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                                                <li>Copia el shortcode de arriba</li>
                                                <li>En WordPress, edita la página o entrada donde quieras mostrar el formulario</li>
                                                <li>Pega el shortcode en el editor (modo texto o bloque de código)</li>
                                                <li>Guarda y publica. El formulario aparecerá automáticamente</li>
                                            </ol>
                                        </div>

                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                            <h5 className="text-sm font-semibold text-gray-800 mb-2">💡 Código del Plugin (si no tienes el plugin):</h5>
                                            <p className="text-xs text-gray-600 mb-2">
                                                Si no tienes el plugin instalado, añade este código al archivo <code>functions.php</code> de tu tema:
                                            </p>
                                            <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs overflow-x-auto">
                                                <pre>{`<?php
function petitprime_iframe_shortcode($atts) {
    $atts = shortcode_atts(array(
        'token' => '',
        'width' => '100%',
        'min_height' => '600px'
    ), $atts);
    
    if (empty($atts['token'])) {
        return '<p>Error: Token requerido</p>';
    }
    
    $embed_url = '${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/embed/registro?token=' . esc_attr($atts['token']);
    $iframe_id = 'petitprime-register-' . uniqid();
    
    return '<iframe id="' . esc_attr($iframe_id) . '" src="' . esc_url($embed_url) . '" style="width: ' . esc_attr($atts['width']) . '; min-height: ' . esc_attr($atts['min_height']) . '; border: 0;"></iframe>';
}
add_shortcode('petitprime_iframe', 'petitprime_iframe_shortcode');
?>`}</pre>
                                            </div>
                                            <button
                                                onClick={() => copyToClipboard(`<?php
function petitprime_iframe_shortcode($atts) {
    $atts = shortcode_atts(array(
        'token' => '',
        'width' => '100%',
        'min_height' => '600px'
    ), $atts);
    
    if (empty($atts['token'])) {
        return '<p>Error: Token requerido</p>';
    }
    
    $embed_url = '${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/embed/registro?token=' . esc_attr($atts['token']);
    $iframe_id = 'petitprime-register-' . uniqid();
    
    return '<iframe id="' . esc_attr($iframe_id) . '" src="' . esc_url($embed_url) . '" style="width: ' . esc_attr($atts['width']) . '; min-height: ' . esc_attr($atts['min_height']) . '; border: 0;"></iframe>';
}
add_shortcode('petitprime_iframe', 'petitprime_iframe_shortcode');
?>`)}
                                                className="mt-2 w-full bg-gray-600 text-white px-3 py-2 rounded text-xs hover:bg-gray-700 transition-colors"
                                            >
                                                <FaCopy className="inline mr-1" />
                                                Copiar Código del Plugin
                                            </button>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        )} */}

                        {/* Botón cerrar */}
                        <div className="mt-4 sm:mt-6 pt-4 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={handleClose}
                                className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmbedFormOptionsModal;

